<template>
  <label
    v-if="label"
    class="label"
  >
    {{ label }}
  </label>
  <div class="select-container">
    <select
      :id="id"
      :value="modelValue"
      class="base-select"
      v-bind="$attrs"
      :required="required"
      @change="handleChange"
    >
      <option v-if="isClearable" selected value="">(Ikke angitt)</option>
      <option
        v-for="option in options"
        :key="option.value"
        :value="option.value"
      >
        {{ option.label }}
      </option>
    </select>
    <div class="select-icons">
      <font-awesome-icon
        v-if="isClearable && modelValue"
        :icon="['fal', 'times']"
        class="clear-icon"
        @click="clearSelection"
      />
      <font-awesome-icon
        :icon="['fal', 'chevron-down']"
        class="chevron-icon"
        tabindex="-1"
      />
    </div>
  </div>
</template>

<script setup>

defineProps({
  modelValue: {
    type: [String, Number, null],
    required: false,
    default: null
  },
  options: {
    type: Array,
    required: true,
    default: () => [],
    validator: (options) => Array.isArray(options) && options.every(option =>
      option && typeof option === 'object' && 'value' in option && 'label' in option
    )
  },
  id: {
    type: String,
    default: ''
  },
  required: {
    type: Boolean,
    default: false
  },
  label: {
    type: String,
    default: ''
  },
  isClearable: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'change']);

const handleChange = (event) => {
  emit('update:modelValue', event.target.value);
  emit('change', event.target.value);
};

const clearSelection = () => {
  emit('update:modelValue', null);
  emit('change', null);
};
</script>

<style scoped>
.select-container {
  position: relative;
  display: inline-block;
  width: 100%;
}

.select-icons {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
  pointer-events: none;
}

.base-select {
  width: 100%;
  border: 1px solid var(--color-border, #ccc);
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding: 8px 70px 8px 8px;
}

.base-select:hover {
  border-color: var(--color-primary);
}

.base-select:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(var(--color-primary-rgb), 0.1);
}

.base-select:required:invalid {
  color: gray;
}

.base-select option {
  color: var(--color-text);
}

.label {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  margin-bottom: 0.2rem;
}

.clear-icon {
  color: var(--gray-500);
  cursor: pointer;
  padding: 8px;
  pointer-events: auto;
}

.clear-icon:hover {
  color: var(--gray-700);
}

.clear-icon:active {
  color: var(--gray-800);
}

.chevron-icon {
  color: var(--gray-600);
  pointer-events: none; /* Klikk går "gjennom" ikonet */
  user-select: none; /* Hindrer markering ved dobbeltklikk */
  outline: none; /* Fjerner visuell highlighting */
}
</style>
